// ============================================================================
// connectionState — "connection lost, retrying" vs "your session ended"
// ============================================================================
//
// SESSION-LOGOUT-WHILE-POLLING-01 / H3.
//
// The 401 interceptor in lib/api.ts used to treat EVERY failure of
// POST /auth/refresh the same way: force-logout, clear the shared token
// storage, broadcast LOGGED_OUT, navigate to /login. That is right for a 401
// (the server saying the refresh token is dead) and wrong for everything else.
// A Railway restart, a cold start, a 502, a request timeout or three seconds of
// dropped Wi-Fi are not statements about the session -- and a user whose
// refresh token is still perfectly valid was being signed out by them.
//
// Failures are now classified. An auth rejection still ends the session. A
// TRANSPORT failure keeps the tokens, retries with backoff, and publishes here
// so the UI can say what is actually happening.
//
// Dependency-free on purpose (no React, no i18n, no '@/lib/api'): lib/api.ts
// imports this, and it is also read from a component. Same one-way-arrow rule
// as lib/authTokenSync and lib/authBroadcast.
// ============================================================================

export type ConnectionStatus = 'online' | 'reconnecting'

export interface ConnectionState {
  status: ConnectionStatus
  /** How many consecutive transport failures the refresh has seen. */
  attempts: number
}

type Listener = (state: ConnectionState) => void

let current: ConnectionState = { status: 'online', attempts: 0 }
const listeners = new Set<Listener>()

function emit(): void {
  // Snapshot the set first: a listener that unsubscribes during its own call
  // must not mutate the collection we are iterating.
  for (const listener of Array.from(listeners)) {
    try {
      listener(current)
    } catch {
      // A throwing subscriber is its own problem. It must not stop the others
      // from hearing about a reconnect, and it must never surface as a failure
      // of the refresh that published it.
    }
  }
}

export function getConnectionState(): ConnectionState {
  return current
}

/**
 * A refresh failed for a TRANSPORT reason and will be retried.
 *
 * `attempts` is the count so far, so the UI can stay quiet on the first blip if
 * it wants to; today's banner shows immediately, because a user watching a
 * multi-hour job would rather see "reconnecting" than a frozen screen.
 */
export function publishReconnecting(attempts: number): void {
  current = { status: 'reconnecting', attempts }
  emit()
}

/**
 * Connectivity is back.
 *
 * Called on any successful response, so recovery does not depend on the refresh
 * that failed being the thing that succeeds next -- an ordinary 200 on any
 * request proves the backend is reachable again.
 */
export function publishOnline(): void {
  if (current.status === 'online' && current.attempts === 0) return
  current = { status: 'online', attempts: 0 }
  emit()
}

export function subscribeConnectionState(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Test-only: drop subscribers and go back to 'online'. */
export function __resetConnectionStateForTests(): void {
  listeners.clear()
  current = { status: 'online', attempts: 0 }
}
